import React from 'react';
import { Tag } from 'antd';
import SeatPill from '../common/SeatPill';
import { KitchenOrderDto, KitchenOrderStatus } from '../../types/kitchen';
import {
  KdsOrderWithTime,
  effectiveStatusOf,
  groupOrderItems,
  isLateOrder,
  parseCourseOptions,
} from './kdsHelpers';

/**
 * Lo que comparten la tarjeta del KDS (carriles) y la comanda colgada (rieles): el estado
 * efectivo, el siguiente paso de la comanda y cómo se pintan sus renglones. Vive aparte para
 * que las dos vistas no puedan divergir en QUÉ hace el botón, sólo en cómo se ve.
 */

export interface KdsOrderFlags {
  isPending: boolean;
  isPreparing: boolean;
  isReady: boolean;
  isCompleted: boolean;
  isLate: boolean;
  isStationOnlyCompleted: boolean;
  isStaleDeadEnd: boolean;
  isDailyMenu: boolean;
}

export const getOrderFlags = (order: KdsOrderWithTime): KdsOrderFlags => {
  const statusStr = effectiveStatusOf(order);
  const isPending = statusStr === KitchenOrderStatus.Pending;
  const isPreparing = statusStr === KitchenOrderStatus.Preparing;
  const isReady = statusStr === KitchenOrderStatus.Ready;
  const isCompleted = statusStr === KitchenOrderStatus.Completed;
  return {
    isPending,
    isPreparing,
    isReady,
    isCompleted,
    isLate: isLateOrder(order.elapsedSeconds) && !isReady && !isCompleted,
    // `isCompleted` above is the EFFECTIVE status: under a production-center filter it only
    // reflects the items this station can see. When it says Completed but the comanda itself is
    // still open, this station is done and another center still owes work — the card has to stay.
    isStationOnlyCompleted: isCompleted && String(order.status) !== KitchenOrderStatus.Completed,
    // A card with nothing left for this station to do, sitting well past the late threshold, is
    // almost always stuck on items routed to a center no screen is watching. Give the line a way
    // out instead of a card that can never be cleared.
    isStaleDeadEnd: isLateOrder(order.elapsedSeconds),
    isDailyMenu: order.isDailyMenuOrder === true,
  };
};

/** Seconds the order has been sitting on the pass, or null when unknown. */
export const passSecondsOf = (order: KitchenOrderDto): number | null => {
  if (!order.readyAt) return null;
  const readyMs = new Date(order.readyAt).getTime();
  if (Number.isNaN(readyMs)) return null;
  return Math.max(0, Math.floor((Date.now() - readyMs) / 1000));
};

export interface KdsOrderHandlers {
  soundEnabled: boolean;
  onAccept: (id: string) => void;
  onMarkReady: (id: string) => void;
  onMarkCompleted: (id: string) => void;
  onDispatchCourse: (orderId: string, itemIds: string[], courseName: string) => void;
  accepting: boolean;
  markingReady: boolean;
  completing: boolean;
}

/**
 * El siguiente paso de una comanda. `tone` dice cómo pintarlo:
 *  - alarm: aceptar con la alarma sonando (ámbar)
 *  - ready: marcar lista (verde)
 *  - primary: aceptar sin alarma, despachar, despachar un tiempo
 *  - danger: salidas de emergencia (forzar despacho, despachar una cancelada)
 */
export type KdsOrderAction =
  | {
      kind: 'button';
      label: string;
      icon: 'sound' | 'check';
      tone: 'alarm' | 'ready' | 'primary' | 'danger';
      loading: boolean;
      run: () => void;
    }
  | { kind: 'text'; text: string }
  | { kind: 'doneBy'; text: string }
  | null;

export const resolveOrderAction = (order: KdsOrderWithTime, h: KdsOrderHandlers): KdsOrderAction => {
  const f = getOrderFlags(order);

  const forceDispatch: KdsOrderAction = {
    kind: 'button',
    label: 'Forzar despacho',
    icon: 'check',
    tone: 'danger',
    loading: h.completing,
    run: () => h.onMarkCompleted(order.id),
  };

  if (f.isCompleted && !f.isStationOnlyCompleted) {
    return { kind: 'doneBy', text: `Despachado por ${order.completedByUserName}` };
  }

  // Everything this station can see is dispatched, but the comanda is still open. Used to
  // render "Despachado por " with an empty name and no button — a card that could never be
  // cleared when the pending items lived in a center nobody watches (El Mirador's COMAL).
  if (f.isStationOnlyCompleted) {
    return f.isStaleDeadEnd ? forceDispatch : { kind: 'text', text: 'Despachado aquí · falta otra estación' };
  }

  // Step 1: Pending → Accept (only when sound is enabled, otherwise skip to Listo/Course)
  if (f.isPending && h.soundEnabled) {
    return {
      kind: 'button', label: 'Aceptar', icon: 'sound', tone: 'alarm',
      loading: h.accepting, run: () => h.onAccept(order.id),
    };
  }

  // Daily-menu per-course dispatch flow. LOAD-BEARING assumption: the backend filters
  // KitchenOrderItems by production center BEFORE projecting items into the DTO — see
  // KitchenService.MapToDto (filteredItems = kitchenOrderItems.Where(PassesProductionCenterFilter)).
  // So `order.items` in a per-station view only contains items routed to that station, and
  // the per-course "next course" detection below picks the right subset.
  if (f.isDailyMenu) {
    // Daily-menu + no alarm + Pending: there's no Aceptar prompt and the classic
    // "Listo" step is skipped for course flow, so expose a plain Aceptar button to
    // advance the card into Preparing and reveal the per-course dispatch button.
    if (f.isPending && !h.soundEnabled) {
      return {
        kind: 'button', label: 'Aceptar', icon: 'check', tone: 'primary',
        loading: h.accepting, run: () => h.onAccept(order.id),
      };
    }

    // For daily-menu cards, compute the "next course" so the button label can show its name
    // (e.g. "Despachar ENTRADA") and the click dispatches only that course's items.
    const pending = order.items.filter(i => !i.isCompleted && !i.isCancelled);
    if (pending.length > 0) {
      const minSort = pending.reduce(
        (m, i) => Math.min(m, i.sortOrder ?? Number.MAX_SAFE_INTEGER),
        Number.MAX_SAFE_INTEGER
      );
      const nextCourse = pending.filter(i => (i.sortOrder ?? Number.MAX_SAFE_INTEGER) === minSort);
      if (nextCourse.length > 0) {
        const courseName = nextCourse[0].productName;
        const ids = nextCourse.map(i => i.id);
        return {
          kind: 'button', label: `Despachar ${courseName}`, icon: 'check', tone: 'primary',
          loading: h.completing, run: () => h.onDispatchCourse(order.id, ids, courseName),
        };
      }
    }

    // All items cancelled from the order detail → kitchen sees the cancelled card
    // and clears it with a manual full-order dispatch.
    const allCancelled = order.items.length > 0 && order.items.every(i => i.isCancelled);
    if (allCancelled) {
      return {
        kind: 'button', label: 'Despachar (cancelada)', icon: 'check', tone: 'danger',
        loading: h.completing, run: () => h.onMarkCompleted(order.id),
      };
    }
    return { kind: 'text', text: 'Actualizando…' };
  }

  // Classic flow (regular cards + daily-menu cards under a production-center filter)
  // Step 1 alternative: Pending → Listo directly (when sound is disabled)
  // Step 2: Preparing → Listo
  if ((f.isPending && !h.soundEnabled) || f.isPreparing) {
    return {
      kind: 'button', label: 'Listo', icon: 'check', tone: 'ready',
      loading: h.markingReady, run: () => h.onMarkReady(order.id),
    };
  }

  // Step 3: Ready → Despachar (hide if all visible items are already completed)
  if (f.isReady) {
    const hasNonCompletedItems = order.items.some(item => !item.isCompleted && !item.isCancelled);
    const allCancelled = order.items.every(item => item.isCancelled);
    if (hasNonCompletedItems || allCancelled) {
      return {
        kind: 'button', label: 'Despachar', icon: 'check', tone: 'primary',
        loading: h.completing, run: () => h.onMarkCompleted(order.id),
      };
    }
    // Same dead end as isStationOnlyCompleted, reached with the order still on Ready.
    return f.isStaleDeadEnd ? forceDispatch : { kind: 'text', text: 'Items de este centro despachados' };
  }

  return null;
};

const DailyMenuItems: React.FC<{ order: KitchenOrderDto }> = ({ order }) => {
  // Sort: pending courses first (by sortOrder asc), then completed ones.
  const sortedCourses = [...order.items].sort((a, b) => {
    const aDone = !!a.isCompleted;
    const bDone = !!b.isCompleted;
    if (aDone !== bDone) return aDone ? 1 : -1;
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });
  // Number of diners = quantity of any (non-cancelled) course; use max as a safe
  // bet in case data is inconsistent. Cancelled courses shouldn't count.
  const dinerCount = sortedCourses
    .filter(c => !c.isCancelled)
    .reduce((m, c) => Math.max(m, c.quantity ?? 0), 0);

  return (
    <>
      {dinerCount > 0 && (
        <div className="kds-course__menu-name">
          {dinerCount}× {order.dailyMenuName || 'Menú del día'}
        </div>
      )}
      {sortedCourses.map(course => {
        const options = parseCourseOptions(course.variants);
        const isDone = !!course.isCompleted;
        const isCxl = !!course.isCancelled;
        const courseClass = isCxl
          ? 'kds-course kds-course--cancelled'
          : isDone
            ? 'kds-course kds-course--done'
            : 'kds-course';
        return (
          <div key={course.id} className={courseClass}>
            <div className="kds-course__label">
              {course.productName}
              {isCxl && <Tag color="error" style={{ margin: 0 }}>CANCELADO</Tag>}
              {isDone && <Tag color="success" style={{ margin: 0 }}>DESPACHADO</Tag>}
            </div>
            {options.length > 0 ? options.map((opt, i) => (
              <div key={i} className="kds-course__option">
                <span className="kds-item__qty">{opt.quantity}×</span>
                {opt.name}
              </div>
            )) : (
              <div className="kds-item__variants" style={{ fontStyle: 'italic' }}>
                (sin opciones)
              </div>
            )}
            {course.notes && (
              <div className="kds-item__note">Nota: {course.notes}</div>
            )}
          </div>
        );
      })}
    </>
  );
};

const RegularItems: React.FC<{ order: KitchenOrderDto }> = ({ order }) => (
  <>
    {groupOrderItems(order.items).map((item, index) => {
      const itemClass = item.isCancelled
        ? 'kds-item kds-item--cancelled'
        : item.isCompleted
          ? 'kds-item kds-item--completed'
          : 'kds-item';
      return (
        <div key={`${item.productName}-${item.variants || ''}-${index}`} className={itemClass}>
          <div className="kds-item__name">
            <span className="kds-item__qty">{item.quantity}×</span>
            {item.seatNumber && <SeatPill seatNumber={item.seatNumber} size="small" />}
            <span className="kds-item__label">{item.productName}</span>
            {item.isCancelled && <Tag color="error" style={{ margin: 0 }}>CANCELADO</Tag>}
            {item.isCompleted && <Tag color="success" style={{ margin: 0 }}>DESPACHADO</Tag>}
          </div>
          {item.variants && (
            <div className="kds-item__variants">({item.variants})</div>
          )}
          {item.notes && (
            <div className="kds-item__note">Nota: {item.notes}</div>
          )}
        </div>
      );
    })}
  </>
);

/** Los renglones de una comanda: tiempos del menú del día o platillos agrupados. */
export const KdsOrderItems: React.FC<{ order: KitchenOrderDto }> = ({ order }) =>
  order.isDailyMenuOrder === true ? <DailyMenuItems order={order} /> : <RegularItems order={order} />;
